import { CASE_STUDIES } from '../data/caseStudies';

export type Locale = 'de' | 'en';

export const SITE = 'https://rokass.org';

export const SOCIAL = {
  github: 'https://github.com/rokas-th',
  linkedin: 'https://www.linkedin.com/in/rokas-sileikis/',
} as const;

export interface Target {
  readonly id: 'home' | 'impressum' | 'datenschutz' | 'case-study';
  readonly locale: Locale;
  readonly path: string;
  readonly index: boolean;
  readonly slug?: string;
}

const LOCALES: readonly Locale[] = ['de', 'en'];

export function homePath(locale: Locale): string {
  return locale === 'en' ? '/en/' : '/';
}

export function sectionPath(locale: Locale, section: string): string {
  return `${homePath(locale)}#${section}`;
}

export function caseStudyPath(locale: Locale, slug: string): string {
  return `${homePath(locale)}work/${slug}/`;
}

const LEGAL_PATHS: Record<'impressum' | 'datenschutz', Record<Locale, string>> = {
  impressum: { de: '/impressum/', en: '/en/imprint/' },
  datenschutz: { de: '/datenschutz/', en: '/en/privacy/' },
};

export function legalPath(locale: Locale, doc: 'impressum' | 'datenschutz'): string {
  return LEGAL_PATHS[doc][locale];
}

const CASE_STUDY_TARGETS: readonly Target[] = Object.keys(CASE_STUDIES).flatMap((slug) =>
  LOCALES.map((locale) => ({ id: 'case-study' as const, locale, path: caseStudyPath(locale, slug), index: true, slug })),
);

export const TARGETS: readonly Target[] = [
  { id: 'home', locale: 'de', path: homePath('de'), index: true },
  { id: 'home', locale: 'en', path: homePath('en'), index: true },
  ...LOCALES.map((locale) => ({ id: 'impressum' as const, locale, path: legalPath(locale, 'impressum'), index: false })),
  ...LOCALES.map((locale) => ({ id: 'datenschutz' as const, locale, path: legalPath(locale, 'datenschutz'), index: false })),
  ...CASE_STUDY_TARGETS,
];

export function localeFromPath(pathname: string): Locale {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'de';
}

export function htmlLang(locale: Locale): string {
  return locale === 'de' ? 'de-CH' : 'en-CH';
}

export function alternates(target: Pick<Target, 'id' | 'slug'>): Record<Locale, string> {
  const pathFor = (locale: Locale): string => {
    if (target.id === 'impressum' || target.id === 'datenschutz') return legalPath(locale, target.id);
    if (target.id === 'case-study' && target.slug) return caseStudyPath(locale, target.slug);
    return homePath(locale);
  };
  return { de: pathFor('de'), en: pathFor('en') };
}

function normalize(pathname: string): string {
  return `${pathname.replace(/\/+$/, '')}/`;
}

export function findTarget(pathname: string): Target | undefined {
  const wanted = normalize(pathname);
  return TARGETS.find((t) => normalize(t.path) === wanted);
}

export function resolveTarget(pathname: string): Target {
  const direct = findTarget(pathname);
  if (direct) return direct;
  const locale = localeFromPath(pathname);
  const home = TARGETS.find((t) => t.id === 'home' && t.locale === locale);
  if (!home) throw new Error(`No home target for locale ${locale}`);
  return home;
}

export function counterpart(pathname: string, locale: Locale): string {
  const target = findTarget(pathname);
  return target ? alternates(target)[locale] : homePath(locale);
}
